import { CaretLeft, CaretRight, Trophy } from "@phosphor-icons/react";

const RANK_TONE = {
  1: "text-amber-300 border-amber-400/40 bg-amber-400/10",
  2: "text-zinc-200 border-zinc-300/30 bg-zinc-300/10",
  3: "text-orange-300 border-orange-400/30 bg-orange-400/10",
};

const shift = (month, delta) => {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const label = (month) => {
  if (!month) return "";
  const [y, m] = month.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
};

/**
 * Monthly affiliate leaderboard — bookings brought in, ranked.
 *
 * SALES-028: this board is reused for the sales POINTS leaderboard. Every
 * addition below is a prop with a DEFAULT that reproduces the affiliate
 * behaviour exactly, so the affiliate page (which passes none of them) renders
 * byte-for-byte as before — no className was touched. The points tab passes
 * `showArrows={false}` (it follows the page's period picker), plus `nameKey`,
 * `metricKey`, `metricUnit`, a `secondary` line, its own heading/subheading and
 * an empty-state message.
 */
export default function LeaderboardBoard({
  board, month, onMonth, testid = "leaderboard",
  nameKey = "name",
  metricKey = "bookings",
  metricUnit = "bookings",
  secondary = (r) => `${r.rental_days} rental days`,
  heading,
  subheading,
  note,
  noteTestid,
  emptyText = "No affiliates yet — nothing to rank.",
  showArrows = true,
}) {
  const rows = board?.rows || [];
  const top = rows.length ? Math.max(...rows.map((r) => r[metricKey] || 0)) : 0;

  return (
    <div className="border border-border bg-card p-6" data-testid={testid}>
      <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
        <div className="flex items-center gap-2">
          <Trophy size={18} className="text-primary" />
          <div>
            <h2 className="font-heading text-2xl">{heading ?? label(month)}</h2>
            <div className="label-mono text-xs text-muted-foreground">
              {subheading ?? `Bookings brought in · ${board?.total_bookings ?? 0} total this month`}
            </div>
          </div>
        </div>
        {showArrows && (
          <div className="flex items-center gap-1">
            <button onClick={() => onMonth(shift(month, -1))} className="p-2 border border-border hover:border-primary/50 transition-colors" data-testid={`${testid}-prev`}>
              <CaretLeft size={14} />
            </button>
            <button onClick={() => onMonth(shift(month, 1))} className="p-2 border border-border hover:border-primary/50 transition-colors" data-testid={`${testid}-next`}>
              <CaretRight size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Optional explanation, INSIDE the card directly under the subheading, so
          it stays visible however long the board gets. Only rendered when a note
          is passed — the affiliate board passes none and is unchanged. Uses the
          existing muted body text style. */}
      {note && (
        <p className="text-sm text-muted-foreground mb-5" data-testid={noteTestid}>{note}</p>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <ul className="space-y-2" data-testid={`${testid}-rows`}>
          {rows.map((r) => (
            <li key={r[nameKey]} className="relative border border-border bg-background/40 overflow-hidden" data-testid={`${testid}-row-${r[nameKey]}`}>
              <div
                className="absolute inset-y-0 left-0 bg-primary/10"
                style={{ width: top ? `${((r[metricKey] || 0) / top) * 100}%` : 0 }}
              />
              <div className="relative flex items-center gap-4 px-4 py-3">
                <span className={`w-9 h-9 shrink-0 rounded-full border flex items-center justify-center font-heading text-sm ${RANK_TONE[r.rank] || "text-muted-foreground border-border"}`}>
                  {r.rank}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-heading truncate">{r[nameKey]}</div>
                  <div className="label-mono text-[10px] text-muted-foreground">{secondary(r)}</div>
                </div>
                <div className="text-right">
                  <div className="font-heading text-xl tnum" data-testid={`${testid}-count-${r[nameKey]}`}>{r[metricKey]}</div>
                  <div className="label-mono text-[10px] text-muted-foreground">{metricUnit}</div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
